import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";

const HERO_IMAGES = {
  T0: "/assets/typologies/t0.webp",
  T1: "/assets/typologies/t1.webp",
  T2: "/assets/typologies/t2.webp",
};

export default function Typologies({ t }) {
  const [active, setActive] = useState("T0");
  const [zoom, setZoom] = useState(null);
  const items = t.typologies.items;

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [zoom]);

  return (
    <section
      id="typologies"
      data-testid="typologies-section"
      className="relative bg-[#0A0A0A] text-white py-24 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-[#BEAF87]" />
            <span className="text-[11px] uppercase tracking-[0.35em] text-[#BEAF87] font-[Barlow_Semi_Condensed] font-medium">
              {t.typologies.eyebrow}
            </span>
          </div>
          <h2
            data-testid="typologies-title"
            className="font-[Cormorant_Garamond] font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            {t.typologies.title}
          </h2>
          <p className="mt-6 text-white/70 font-[Barlow_Semi_Condensed] max-w-xl">
            {t.typologies.subtitle}
          </p>
        </div>

        <Tabs value={active} onValueChange={setActive} className="w-full">
          <TabsList
            data-testid="typologies-tabs"
            className="bg-transparent p-0 h-auto gap-2 border-b border-white/15 w-full justify-start rounded-none"
          >
            {items.map((it) => (
              <TabsTrigger
                key={it.code}
                value={it.code}
                data-testid={`typology-tab-${it.code}`}
                className="px-6 py-3 rounded-none text-white/50 data-[state=active]:bg-transparent data-[state=active]:text-[#BEAF87] data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-[#BEAF87] uppercase tracking-[0.2em] text-xs font-[Barlow_Semi_Condensed] font-medium"
              >
                {it.code}
              </TabsTrigger>
            ))}
          </TabsList>

          {items.map((it) => (
            <TabsContent
              key={it.code}
              value={it.code}
              data-testid={`typology-panel-${it.code}`}
              className="mt-12"
            >
              <div className="grid lg:grid-cols-12 gap-10 items-center">
                <button
                  type="button"
                  onClick={() => setZoom(it)}
                  aria-label={it.name}
                  data-testid={`typology-plan-${it.code}`}
                  className="lg:col-span-7 block w-full relative overflow-hidden bg-[#EFE9DD] cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BEAF87]"
                >
                  <img
                    src={HERO_IMAGES[it.code] || HERO_IMAGES.T0}
                    alt={it.name}
                    className="w-full h-[420px] lg:h-[560px] object-cover"
                  />
                </button>

                <div className="lg:col-span-5">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[#BEAF87] font-[Barlow_Semi_Condensed] mb-3">
                    {it.code}
                  </div>
                  <h3 className="font-[Cormorant_Garamond] text-4xl lg:text-5xl font-light leading-tight">
                    {it.name}
                  </h3>
                  <p className="mt-6 text-white/70 font-[Barlow_Semi_Condensed] text-base leading-relaxed">
                    {it.desc}
                  </p>

                  <dl className="mt-10 space-y-5">
                    <Row k={t.typologies.common.area} v={it.area} />
                    <Row k={t.typologies.common.balcony} v={it.balcony} />
                    <Row k={t.typologies.common.garage} v={it.garage} />
                  </dl>

                  <div className="mt-10 pt-8 border-t border-white/10 flex items-baseline justify-between">
                    <span className="text-[11px] uppercase tracking-[0.3em] text-white/50 font-[Barlow_Semi_Condensed]">
                      {t.typologies.common.from}
                    </span>
                    <span className="font-[Cormorant_Garamond] text-4xl text-[#BEAF87]">
                      {it.from}
                    </span>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {zoom && (
        <div
          data-testid="typology-plan-modal"
          role="dialog"
          aria-modal="true"
          aria-label={zoom.name}
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A0A0A]/95 backdrop-blur-sm p-6 animate-fade-up"
        >
          <button
            type="button"
            onClick={() => setZoom(null)}
            data-testid="typology-plan-close"
            aria-label="Fechar"
            className="absolute top-5 right-5 lg:top-8 lg:right-8 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white hover:border-[#BEAF87] hover:text-[#BEAF87] transition-colors"
          >
            <X size={20} />
          </button>
          <figure
            onClick={(e) => e.stopPropagation()}
            className="flex flex-col items-center max-w-[95vw] max-h-[90vh]"
          >
            <img
              src={HERO_IMAGES[zoom.code] || HERO_IMAGES.T0}
              alt={zoom.name}
              className="max-h-[82vh] max-w-full object-contain bg-[#EFE9DD] shadow-2xl"
            />
            <figcaption className="mt-4 text-center text-sm text-white/70 font-[Barlow_Semi_Condensed]">
              {zoom.name}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between border-b border-white/10 pb-4">
      <dt className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-[Barlow_Semi_Condensed]">
        {k}
      </dt>
      <dd className="font-[Barlow_Semi_Condensed] text-white text-lg">{v}</dd>
    </div>
  );
}
